import { getAxiosInstance } from '@/services/config';
import { UserController_Me } from '@/services/services';

import { RequestError } from '../shared/errors';

/**
 * Calls that answer 401 for a reason other than an ended session: a wrong password, or a
 * request that needs no session at all. A 401 from any of them is the caller's to show.
 */
const NO_SESSION_ENDPOINTS = new Set([
    '/api/v2/user/login',
    '/api/v2/user/register',
    '/api/v2/user/reset-password',
    '/api/v2/user/reset-password-request',
]);
const NO_SESSION_PREFIX = '/api/v2/auth/';

function requestPath(url: string | undefined): string | undefined {
    if (!url) return undefined;
    try {
        return new URL(url, 'http://portal.invalid').pathname;
    } catch {
        return undefined;
    }
}

/** The call the app uses to ask who is signed in; a 401 from it is the session's own answer. */
const SESSION_ENDPOINT = UserController_Me.key;

type Verdict = 'ended' | 'check' | 'ignore';

/**
 * What a failed API call says about the session. A 401 from the session endpoint means it has
 * ended. A 401 from anything else only might: the backend also answers 401 when a signed-in
 * user lacks access to a client or a pen, so the session has to be asked before anyone is
 * signed out.
 */
function verdict(error: unknown): Verdict {
    if (!RequestError.isRequestError(error) || error.status !== 401) return 'ignore';
    const path = requestPath(error.response?.config.url);
    if (path === undefined) return 'ignore';
    if (NO_SESSION_ENDPOINTS.has(path) || path.startsWith(NO_SESSION_PREFIX)) return 'ignore';
    return path === SESSION_ENDPOINT ? 'ended' : 'check';
}

/** Asks the backend whether the session still holds. Anything but a 401 counts as yes. */
async function sessionHolds(): Promise<boolean> {
    try {
        await UserController_Me();
        return true;
    } catch (error: unknown) {
        return !(RequestError.isRequestError(error) && error.status === 401);
    }
}

export interface SessionExpiryOptions {
    /**
     * Whether the app holds a signed-in user. While nobody is signed in (the login, sign-up
     * and reset pages), a 401 is the expected answer and nothing has expired.
     */
    isSignedIn: () => boolean;
    /** Called once, for the first call that finds the session gone. */
    onExpired: () => void;
    /** Whether the session still holds. Defaults to asking the session endpoint. */
    sessionStillValid?: () => Promise<boolean>;
}

/**
 * Watches every API call for a 401 that means the session ended while the app still holds a
 * user, and reports it once however many calls fail together. A 401 that may only mean "no
 * access" is confirmed with one session check first, never several at once. The call still
 * rejects, so its own error handling runs as before. Returns a function that stops watching.
 */
export function watchSessionExpiry(options: SessionExpiryOptions): () => void {
    const { isSignedIn, onExpired, sessionStillValid = sessionHolds } = options;
    let reported = false;
    let checking = false;
    const client = getAxiosInstance(undefined);

    const report = () => {
        if (reported || !isSignedIn()) return;
        reported = true;
        onExpired();
    };

    const interceptor = client.interceptors.response.use(undefined, (error: unknown) => {
        if (reported || !isSignedIn()) return Promise.reject(error);
        const result = verdict(error);
        if (result === 'ended') {
            report();
        } else if (result === 'check' && !checking) {
            checking = true;
            // A check that fails without an answer says nothing about the session; stay signed in.
            void sessionStillValid()
                .then(
                    (holds) => {
                        if (!holds) report();
                    },
                    () => undefined
                )
                .finally(() => {
                    checking = false;
                });
        }
        return Promise.reject(error);
    });

    return () => client.interceptors.response.eject(interceptor);
}
